import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { CustomerService } from '../../core/services/customer.service';
import { Customer, PagedCustomersRequest, PagedCustomersResponse } from '../../core/models/customer.model';
import { IconFieldModule } from 'primeng/iconfield';
import { Menu } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { InputIcon } from 'primeng/inputicon';
import { Router, ActivatedRoute } from '@angular/router';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';
import { SubscriberService } from '../../core/services/subscriber.service';
import { EncryptionService } from '../../core/services/encryption';
import { SETTINGS } from '../../core/config/common.settings';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { FileIconService, FileIconInfo } from '../../core/services/file-icon.service';

@Component({
  selector: 'app-customer',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    PaginatorModule,
    MenuModule,
    ConfirmDialogModule,
    ToastModule,
    CardModule,
    BadgeModule,
    AvatarModule,
    TooltipModule,
    DrawerModule,
    TagModule,
    IconFieldModule,
    InputIcon,
    HasPermissionDirective,
    LoadingSpinnerComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './customer.html',
  styleUrl: './customer.scss'
})
export class CustomerComponent implements OnInit {
  @ViewChild('menu') menu!: Menu;
  
  customers: Customer[] = [];
  totalCustomers = 0;
  loading = false;
  currentPage = 1;
  pageSize = 10;
  pageSizes = [10, 20, 50, 100];
  
  Math = Math;
  
  // Filters
  searchText = '';
  selectedIndustry: any = null;
  selectedInterestLevel: any = null;
  selectedPromotionalGifts: any = null;
  
  // FSR filtering
  isFsrFilter = false;
  fsrId: string | null = null;
  fsrName = '';
  
  // Sorting
  sortField = 'createdAt';
  sortOrder: 1 | -1 = -1; // -1 for descending (newest first), 1 for ascending (oldest first)

  industryOptions = [
    { label: 'All Industries', value: null }
  ];

  interestLevelOptions = [
    { label: 'Interest Levels', value: null },
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
  ];

  // promotionalGiftsOptions = [
  //   { label: 'All', value: null },
  //   { label: 'Accepted', value: true },
  //   { label: 'Not Accepted', value: false }
  // ];

  // Customer actions menu
  actionMenuItems: MenuItem[] = [];
  selectedCustomer: Customer | null = null;

  // Drawer for customer review
  isDrawerVisible = false;
  selectedCustomerForReview: Customer | null = null;
  customerReviewLoading = false;
  customerDocuments: string[] = [];

  // Additional loading states
  deletingCustomer = false;
  loadingIndustries = false;

  // Current user
  currentUser: any = null;

  constructor(
    private customerService: CustomerService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute,
    private subscriberService: SubscriberService,
    private encryption: EncryptionService,
    private fileIconService: FileIconService
  ) {}

  ngOnInit() {
    // Load current user
    this.loadCurrentUser();
    
    // Check for FSR filter parameters from query params (for admin viewing specific FSR's customers)
    // Check synchronously first using snapshot
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['fsrFilter'] === 'true' && queryParams['fsrId']) {
      this.isFsrFilter = true;
      this.fsrId = queryParams['fsrId'];
      this.loadFsrInfo();
    } else if (this.isFSR() && this.currentUser?._id) {
      // If current user is FSR, set filter status for UI display (backend handles actual filtering)
      this.isFsrFilter = true;
      this.fsrId = this.currentUser._id;
      this.fsrName = this.currentUser.firstName && this.currentUser.lastName 
        ? `${this.currentUser.firstName} ${this.currentUser.lastName}`
        : this.currentUser.email || 'You';
    }
    
    // Also subscribe to query param changes for dynamic updates
    this.route.queryParams.subscribe(params => {
      if (params['fsrFilter'] === 'true' && params['fsrId']) {
        this.isFsrFilter = true;
        this.fsrId = params['fsrId'];
        this.loadFsrInfo();
        this.loadCustomers(); // Reload customers when filter changes
      } else if (params['fsrFilter'] === 'false' || !params['fsrFilter']) {
        // Clear FSR filter if explicitly set to false (only for admin users)
        if (!this.isFSR()) {
          this.isFsrFilter = false;
          this.fsrId = null;
          this.fsrName = '';
          this.loadCustomers();
        }
      }
    });
    
    this.loadIndustries();
    this.loadCustomers();
    this.setupActionMenu();
  }

  // Load current user from localStorage
  loadCurrentUser(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      this.currentUser = JSON.parse(userStr);
    }
  }

  // Check if current user is FSR
  isFSR(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    return roleIdentification === 'FSR';
  }

  // Check if current user is SA
  isSA(): boolean {
    const roleIdentification = this.currentUser?.roleId?.identification || this.currentUser?.role?.identification;
    
    return roleIdentification === 'SA' || roleIdentification === 'SUPER_DEV_ADMIN';
  }

  loadIndustries(): void {
    this.loadingIndustries = true;
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      
      const subscriberId = user.subscriberId?._id || user.subscriberId;
      
      if (subscriberId) {
        this.subscriberService.findSubscriber(subscriberId).then((subscriber: any) => {
          let actualSubscriber = subscriber;
          if (subscriber && subscriber.subscriber) {
            actualSubscriber = subscriber.subscriber;
          }
          
          if (actualSubscriber && actualSubscriber.industries && actualSubscriber.industries.length > 0) {
            this.industryOptions = [
              { label: 'All Industries', value: null },
              ...actualSubscriber.industries.map((industry: string) => ({
                label: industry,
                value: industry
              }))
            ];
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Configuration Notice',
              detail: 'No industries are configured for your company.'
            });
            // Keep only "All Industries" option
            this.industryOptions = [{ label: 'All Industries', value: null }];
          }
          this.loadingIndustries = false;
        }).catch((error: any) => {
          console.error('Error loading industries:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load industry options. Please refresh the page or contact support if the issue persists.'
          });
          this.industryOptions = [{ label: 'All Industries', value: null }];
          this.loadingIndustries = false;
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No subscriber information available. Please log in again.'
        });
        this.industryOptions = [{ label: 'All Industries', value: null }];
        this.loadingIndustries = false;
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No user information available. Please log in again.'
      });
      this.industryOptions = [{ label: 'All Industries', value: null }];
      this.loadingIndustries = false;
    }
  }

  setupActionMenu() {
    this.actionMenuItems = [];
  }

  updateActionMenuItems(customer: Customer) {
    this.actionMenuItems = [
      {
        label: 'View Details',
        icon: 'pi pi-info-circle',
        command: () => this.openCustomerReview(customer._id)
      },
      {
        label: 'Edit Customer',
        icon: 'pi pi-pencil',
        command: () => this.editCustomer(customer)
      },
      // {
      //   label: 'Delete Customer',
      //   icon: 'pi pi-trash',
      //   command: () => this.deleteCustomer(),
      //   styleClass: 'text-red-600 hover:text-red-700'
      // }
    ];
  }

  loadCustomers() {
    this.loading = true;
    const request: PagedCustomersRequest = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      filters: {
        searchText: this.searchText,
        industry: this.selectedIndustry,
        interestLevel: this.selectedInterestLevel,
        acceptedPromotionalGifts: this.selectedPromotionalGifts,
        // Only send FSR filter if explicitly set via query params (for admin viewing specific FSR)
        // Backend automatically applies FSR filter if current user is FSR
        fsrFilter: this.isFsrFilter && !this.isFSR() ? true : undefined,
        fsrId: this.isFsrFilter && !this.isFSR() ? (this.fsrId || undefined) : undefined
      }
    };

    this.customerService.getPagedCustomers(request).subscribe({
      next: (response: PagedCustomersResponse) => {
        console.log('Customers response:', response);
        this.customers = response.data || [];
        this.totalCustomers = response.metadata?.[0]?.total || 0;
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error loading customers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customers'
        });
        this.loading = false;
      }
    });
  }

  applyFilters() {
    this.currentPage = 1;
    this.loadCustomers();
  }

  resetFilters() {
    this.searchText = '';
    this.selectedIndustry = null;
    this.selectedInterestLevel = null;
    this.selectedPromotionalGifts = null;
    this.currentPage = 1;
    this.resetSorting();
    
    // Don't reset FSR filter when resetting other filters
    this.loadCustomers();
  }

  resetSorting() {
    this.sortField = 'createdAt';
    this.sortOrder = -1;
  }

  toggleSort() {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.loadCustomers();
  }

  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadCustomers();
  }

  // Open drawer for customer review
  openCustomerReview(customerId: string) {
    this.isDrawerVisible = true;
    this.customerReviewLoading = true;
    this.selectedCustomerForReview = null;

    // Find the customer from the existing table data first
    const existingCustomer = this.customers.find(customer => customer._id === customerId);
    
    if (existingCustomer) {
      // Use the existing data
      this.selectedCustomerForReview = existingCustomer;
      this.customerDocuments = this.getCustomerDocuments(existingCustomer);
      this.customerReviewLoading = false;
      console.log('Selected customer for review (from table):', this.selectedCustomerForReview);
    } else {
      // Fallback to API call if not found in table
      this.customerService.getCustomerById(customerId).subscribe({
        next: (response: any) => {
          const customer = response.customer || response;
          this.selectedCustomerForReview = customer;
          this.customerDocuments = this.getCustomerDocuments(customer);
          this.customerReviewLoading = false;
          console.log('Selected customer for review (from API):', this.selectedCustomerForReview);
        },
        error: (error) => {
          console.error('Error loading customer details:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load customer details'
          });
          this.customerReviewLoading = false;
          this.isDrawerVisible = false;
        }
      });
    }
  }
    
  // Close drawer
  closeCustomerReview() {
    this.isDrawerVisible = false;
    this.selectedCustomerForReview = null;
    this.customerDocuments = [];
    this.customerReviewLoading = false;
  }

  getInterestLevelBadgeSeverity(interestLevel: string): 'success' | 'warn' | 'danger' | 'secondary' | 'info' {
    switch (interestLevel?.toLowerCase()) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warn';
      case 'low':
        return 'danger';
      default:
        return 'info';
    }
  }

  getInterestLevelLabel(interestLevel: string): string {
    return interestLevel || 'N/A';
  }

  showActionMenu(event: Event, customer: Customer) {
    this.selectedCustomer = customer;
    this.updateActionMenuItems(customer);
    event.stopPropagation();
    if (this.menu) {
      this.menu.show(event);
    }
  }

  editCustomer(customer: Customer) {
    if (customer) {
      this.router.navigate(['/admin/add-edit-customer'], {
        queryParams: {
          mode: 'edit',
          id: customer._id,
          returnUrl: '/admin/customers'
        }
      });
    }
  }

  deleteCustomer() {
    if (this.selectedCustomer) {
      this.confirmationService.confirm({
        message: `Are you sure you want to delete customer "${this.selectedCustomer.businessName}"?`,
        header: 'Confirm Delete',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.deletingCustomer = true;
          this.customerService.deleteCustomer(this.selectedCustomer!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Customer deleted successfully'
              });
              this.loadCustomers();
              this.deletingCustomer = false;
            },
            error: (error) => {
              console.error('Error deleting customer:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete customer'
              });
              this.deletingCustomer = false;
            }
          });
        }
      });
    }
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  }

  openInNewTab(url: string) {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  formatDate(dateString: string | Date): string {
    if (!dateString) return 'N/A';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  addNewCustomer() {
    this.router.navigate(['/admin/customer-lookup']);
  }

  private loadFsrInfo() {
    // Load FSR name for display
    if (this.fsrId) {
      // You can expand this to load actual FSR info if needed
      this.fsrName = 'Loading...';
      // For now, we'll just show a generic message
      setTimeout(() => {
        this.fsrName = 'Field Sales Representative';
      }, 100);
    }
  }

  clearFsrFilter() {
    // Prevent FSR users from clearing their filter - they should always see only their customers
    if (this.isFSR()) {
      return;
    }
    
    this.isFsrFilter = false;
    this.fsrId = null;
    this.fsrName = '';
    // Navigate back to regular customers view
    this.router.navigate(['/admin/customers']);
  }
  
  viewCustomer(customerId: string) {
    this.router.navigate(['/admin/view-customer', customerId]);
  }

  // Helper method to get documents as an array (handles both string and array formats)
  getCustomerDocuments(customer: Customer | null): string[] {
    if (!customer || !customer.documents) {
      return [];
    }
    
    // If documents is already an array, return it
    if (Array.isArray(customer.documents)) {
      return customer.documents.filter(doc => doc && typeof doc === 'string' && doc.trim() !== '');
    }
    
    // Ensure documents is a string before processing
    if (typeof customer.documents !== 'string') {
      return [];
    }
    
    // If documents is a string, check if it's a comma-separated list or JSON array
    const docs = customer.documents.trim();
    
    // Try to parse as JSON array first
    try {
      const parsed = JSON.parse(docs);
      if (Array.isArray(parsed)) {
        return parsed.filter(doc => doc && doc.trim() !== '');
      }
    } catch (e) {
      // Not JSON, continue
    }
    
    // Check if it's a comma-separated string
    if (docs.includes(',')) {
      return docs.split(',').map(doc => doc.trim()).filter(doc => doc !== '');
    }
    
    // Single document string
    return [docs];
  }

  // Get file icon information
  getFileIcon(fileUrl: string): FileIconInfo {
    return this.fileIconService.getFileIcon(fileUrl);
  }

  // Get document display name
  getDocumentDisplayName(documentUrl: string, index: number): string {
    if (!documentUrl) return '';
    
    // Try to extract filename from URL
    try {
      const urlParts = documentUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      // Remove query parameters if any
      const cleanFilename = filename.split('?')[0];
      if (cleanFilename && cleanFilename.length > 0) {
        return cleanFilename;
      }
    } catch (e) {
      // Fallback to generic name
    }
    
    return `Attachment ${index + 1}`;
  }

  // Open document in new tab
  openDocument(documentUrl: string): void {
    if (!documentUrl) return;
    
    // Ensure full URL if relative
    const fullUrl = documentUrl.startsWith('http') 
      ? documentUrl 
      : `https://purpledev.s3.us-east-2.amazonaws.com/office-global/${documentUrl}`;
    
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }
}
